import { useMemo, useRef, useState } from 'react';
import { NJButton, NJIconButton, NJMenu, NJToastContainer, NJToastService, NJTooltip } from '@engie-group/fluid-design-system-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { AgGridReact } from 'ag-grid-react';
import { NetworkShell, SIZE2MIX_AG_GRID_LOCALE, SIZE2MIX_AG_GRID_PAGE_SIZES, Size2MixStatusTag, TEST_PUC_NETWORK, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { ImportDrawer } from './ImportDrawer';
import { useImportExport, useImportExportActions } from './hooks';
import type { ImportExportKind, ImportExportRow } from './types';

export const meta = {
    title: 'Référence — Réseau > Import/export',
    description:
        "Page de référence « Import/export » d'un réseau (test PUC) mesurée sur integration : listes AG Grid (flex, tri date desc, pagination 15/30/50/100), tiroir « Importer un réseau » (OpenGIS / NEMO), menu Exporter, téléchargement, suppression + toast.",
    tags: ['network', 'list', 'reference'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'import-export',
    createdAt: '2026-10-10',
};

/** Parses Nemo dates `YYYY/MM/DD H:mm` (hour not zero-padded, so a string sort is wrong). */
function dateValue(value?: string) {
    const match = value?.match(/^(\d{4})\/(\d{2})\/(\d{2}) (\d{1,2}):(\d{2})$/);
    return match ? Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5]) : 0;
}

/** Action menus: close on click (NJMenu.Selection) without keeping a selected item. */
const NO_SELECTION: number[] = [];

const toastSuccess = (message: string) =>
    NJToastService(message, {
        iconName: 'check_circle',
        dismissAfter: 4000,
        hasCloseIcon: true,
    });

function download(row: ImportExportRow, fileName: string) {
    const blob = new Blob([JSON.stringify({ network: TEST_PUC_NETWORK.name, task: row }, null, 2)], { type: 'application/zip' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
}

/** Download menu items per row type (production `ActionsCellRender` → `downloadItems`). */
function downloadItems(kind: ImportExportKind, row: ImportExportRow): { label: string; fileName: string }[] {
    if (kind === 'exports') return [{ label: 'Export du réseau', fileName: `${TEST_PUC_NETWORK.name}_export` }];
    const items = [{ label: "Données d'entrée", fileName: 'zip_file' }];
    return row.type === 'Import OpenGIS' ? [...items, { label: 'Fichiers csv. intermédiaires', fileName: 'intermidiate_csv' }] : items;
}

function ActionIcon({ icon, label, tooltip, onClick }: { icon: string; label: string; tooltip: string; onClick?: () => void }) {
    return (
        <NJTooltip text={tooltip} hasArrow isColorInverse={false} placement="left">
            <NJIconButton icon={icon} label={label} variant="secondary" scale="xs" onClick={onClick} />
        </NJTooltip>
    );
}

export default function RefNetworkImportExport() {
    const nav = useSize2MixRefNavigation();
    const { data } = useImportExport();
    const actions = useImportExportActions();
    // Mutation objects change on every render: keep column defs stable so AG Grid keeps the user's sort.
    const actionsRef = useRef(actions);
    actionsRef.current = actions;
    const [importOpen, setImportOpen] = useState(false);

    const columns = useMemo(() => {
        const build = (kind: ImportExportKind): ColDef<ImportExportRow>[] => [
            {
                headerName: 'Statut',
                field: 'status',
                cellRenderer: (p: ICellRendererParams<ImportExportRow, string>) => <Size2MixStatusTag label={p.value ?? ''} />,
            },
            {
                headerName: 'Nom',
                field: 'name',
                flex: 2,
                cellRenderer: (p: ICellRendererParams<ImportExportRow, string>) => (
                    // Production links to the job logs page (/import-export/:id), not reproduced here.
                    <a
                        href="#"
                        className="nj-link nj-link--contextual"
                        title={p.value ?? undefined}
                        onClick={(event) => event.preventDefault()}
                        style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            verticalAlign: 'middle',
                            fontSize: 14,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {p.value}
                    </a>
                ),
            },
            { headerName: 'Type', field: 'type' },
            {
                headerName: 'Date',
                field: 'date',
                sort: 'desc',
                comparator: (a: string, b: string) => dateValue(a) - dateValue(b),
            },
            {
                headerName: 'Action',
                sortable: false,
                cellRenderer: ({ data: row }: ICellRendererParams<ImportExportRow>) => {
                    if (!row) return null;
                    const target = { kind, id: row.id };
                    // Production `ActionsCellRender`: restart if failed, download if finished, interrupt if running, then always `|` + delete.
                    const primary = [
                        row.status === 'Échec' && (
                            <ActionIcon key="restart" icon="autorenew" label="restart" tooltip="Redémarrer" onClick={() => actionsRef.current.restart.mutate(target)} />
                        ),
                        row.status === 'Succès' && (
                            <NJTooltip key="download" text={kind === 'imports' ? "Télécharger les données d'entrée" : "Télécharger le fichier d'export"} hasArrow placement="left">
                                <span style={{ display: 'inline-flex' }}>
                                    <NJMenu.Root>
                                        <NJMenu.Anchor>
                                            <NJIconButton icon="download" label="download" variant="secondary" scale="xs" />
                                        </NJMenu.Anchor>
                                        <NJMenu.Dropdown>
                                            <NJMenu.Selection selectedIndexes={NO_SELECTION}>
                                                <NJMenu.Group>
                                                    {downloadItems(kind, row).map((item) => (
                                                        <NJMenu.Item key={item.fileName} onClick={() => download(row, item.fileName)}>
                                                            {item.label}
                                                        </NJMenu.Item>
                                                    ))}
                                                </NJMenu.Group>
                                            </NJMenu.Selection>
                                        </NJMenu.Dropdown>
                                    </NJMenu.Root>
                                </span>
                            </NJTooltip>
                        ),
                        row.status === 'En Cours' && (
                            <ActionIcon key="stop" icon="stop_circle" label="stop" tooltip="Interrompre" onClick={() => actionsRef.current.stop.mutate(target)} />
                        ),
                    ];
                    return (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            {primary}
                            <span aria-hidden="true" style={{ margin: '0 8px' }}>
                                |
                            </span>
                            <ActionIcon
                                icon="delete_outline"
                                label="delete"
                                tooltip="Supprimer"
                                // Measured: deletion is immediate, without confirmation.
                                onClick={() =>
                                    actionsRef.current.remove.mutate(target, {
                                        onSuccess: () => toastSuccess('Élément supprimé avec succès'),
                                    })
                                }
                            />
                        </span>
                    );
                },
            },
        ];
        return { imports: build('imports'), exports: build('exports') };
    }, []);

    const section = (kind: ImportExportKind, title: string, button: React.ReactNode) => {
        const rows = data?.[kind] ?? [];
        return (
            <div style={{ padding: 20 }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <b>{`${title}\u00a0(${rows.length})`}</b>
                    {button}
                </div>
                <div className="ag-theme-quartz" style={{ marginTop: 16 }} data-testid={`${kind}_table`}>
                    <AgGridReact<ImportExportRow>
                        rowData={rows}
                        getRowId={(p) => p.data.id}
                        columnDefs={columns[kind]}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 150,
                            sortable: true,
                            resizable: false,
                            suppressMovable: true,
                            // Production `headerStyle`: tertiary text, 14px bold.
                            headerClass: 'ref-ie-header',
                        }}
                        domLayout="autoHeight"
                        localeText={SIZE2MIX_AG_GRID_LOCALE}
                        pagination
                        paginationPageSize={15}
                        paginationPageSizeSelector={SIZE2MIX_AG_GRID_PAGE_SIZES}
                    />
                </div>
            </div>
        );
    };

    const startExport = (format: 'NEMO' | 'OpenGIS') => actions.startExport.mutate(format, { onSuccess: () => toastSuccess('Élément ajouté avec succès') });

    const exportMenu = (
        <NJMenu.Root>
            <NJMenu.Anchor>
                <NJButton label="Exporter" />
            </NJMenu.Anchor>
            <NJMenu.Dropdown>
                <NJMenu.Selection selectedIndexes={NO_SELECTION}>
                    <NJMenu.Group>
                        {/* Measured: choosing a format starts the export immediately (no form). */}
                        <NJMenu.Item onClick={() => startExport('NEMO')}>Au format NEMO</NJMenu.Item>
                        <NJMenu.Item onClick={() => startExport('OpenGIS')}>Au format OpenGIS</NJMenu.Item>
                    </NJMenu.Group>
                </NJMenu.Selection>
            </NJMenu.Dropdown>
        </NJMenu.Root>
    );

    return (
        <PrototypeStudioLayout title={meta.title}>
            <NetworkShell network={TEST_PUC_NETWORK} activePage="import-export" onNavigate={nav.onNetworkNavigate} onPrimaryNavigate={nav.onPrimaryNavigate}>
                <div style={{ padding: 20 }}>
                    {section('imports', 'Liste des imports', <NJButton label="Importer" onClick={() => setImportOpen(true)} />)}
                    {section('exports', 'Liste des exports', exportMenu)}
                </div>
                <ImportDrawer isOpen={importOpen} onClose={() => setImportOpen(false)} onSubmit={(request) => actions.startImport.mutate(request)} />
            </NetworkShell>
            <style>{`.ref-ie-header .ag-header-cell-text { color: var(--nj-semantic-color-text-neutral-tertiary-default); font-size: 14px; font-weight: 700; }`}</style>
            <NJToastContainer />
        </PrototypeStudioLayout>
    );
}
